import { useState, useEffect, useCallback } from "react";
import { observer } from "mobx-react-lite";
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Chip from '@mui/joy/Chip';
import Card from '@mui/joy/Card';
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Input from "@mui/joy/Input";
import Divider from "@mui/joy/Divider";


import Button from "@mui/joy/Button";
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';


import useRoles from "hooks/useRoles";
import useUser from 'hooks/useUser';
import useIdentity from 'hooks/useIdentity';
import useWorkKey from 'hooks/useWorkKey';


import { UserProps } from 'stores/user_store';
import { IdentityProps } from 'stores/identity_store';
import DateUI from 'components/Date';
import IdentityAvatar from 'components/IdentityAvatar';
import SupportTeamActions from 'components/SupportTeamActions';

import Link from "components/Link";
import getIcon from "utils/getIcon";



const SupportUserAvatar = observer(({user}:{user: UserProps}) => {
    const { rolesNames } = useRoles();
    const { supportFetchIdentityForUser } = useIdentity();
    const [fetchingIdentity, setFetchingIdentity] = useState(false);
    const [identityKey, setIdentityKey] = useState('');
    //    const [noIdentity, setNoIdentity] = useState(false);
    const [didFetch, setDidFetch] = useState(false);

    const localFetchIdentity = useCallback(() => {
        if(didFetch) {
            return;
        }

        setFetchingIdentity(true);
        setIdentityKey('');

        supportFetchIdentityForUser(user.key)
            .then((result: IdentityProps) => {
                setIdentityKey(result?.key);
                setFetchingIdentity(false);
            })
            .catch(() => {
                // setNoIdentity(true);
                setFetchingIdentity(false);
            })
            .finally(() => {
                setDidFetch(true);
            });
    }, [supportFetchIdentityForUser, user.key, didFetch, setDidFetch]);

    useEffect(() => {
        localFetchIdentity();
    }, [localFetchIdentity, user.key]);


    if(!rolesNames.includes('support'))
        return null;

    return (
        <Stack
            divider={<Divider />}
            spacing={1}
        >
            <Typography
                color="success"
                level="title-md">
                Found user!
            </Typography>

            <Card>
            <Typography level="title-lg">

            <Link
                to={`/support/user/${user.uuid || user.key}`}>
                {user.name}
            </Link>
            </Typography>

            <Stack spacing={1}>
                <Typography level="title-sm">
                    User Details
                </Typography>
                <Stack>
                    {(user.first_name || user.last_name) ?
                        <Typography level="body-sm">
                            {user.first_name} {user.last_name}
                        </Typography>
                        :
                        <Typography level="body-sm">
                            Name Not Set
                        </Typography>
                    }

                    <Typography level="body-sm">
                                                    Primary Email:
            <Link
                to={`/support/user/${user.uuid || user.key}`}>
                <Chip>{user.primary_email}</Chip></Link>
                    </Typography>
                    <Typography
                        level="body-xs">
                        <DateUI dateString={user.date_created} />
                    </Typography>
                </Stack>

                {fetchingIdentity && "Fetching Identity"}

                {identityKey &&
                    <Stack>
                        <Typography level="title-sm">
                            Day Ones Identity
                        </Typography>
                        <Typography level="body-xs">
                            This person has an account on Day Ones
                        </Typography>
                        <IdentityAvatar size="chip"
                            interactive={"link"}
                            identityKey={identityKey} />
                    </Stack>
                }
                </Stack>
                </Card>
        </Stack>
    );
});


export const SupportUserSearch = observer(() => {
    const { rolesNames } = useRoles();
    const [query, setQuery] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const { supportEmailSearch } = useUser();
    const workKey = useWorkKey();
    const [results, setResults] = useState([] as UserProps[]);
    if(!rolesNames.includes('support'))
        return null;

    function doSearch() {
        if (!workKey) return;
        setIsSearching(true);
        setResults([] as UserProps[]);
        supportEmailSearch(workKey,
            query).then((_results: UserProps[]) => {
                setResults(_results);
                setIsSearching(false);
            });
    }

    return (
        <Stack>
            <Stack direction="row"
                spacing={1}
                p={1}
                alignItems="flex-end"
            >
                <FormControl>
                    <FormLabel>Search Users By Email</FormLabel>
                    <Input
                        onChange={(e) => {
                            setQuery(e.target.value);
                            setIsSearching(false);
                            setResults([] as UserProps[])
                        }}
                        value={query}
                        name="query"
                        placeholder="martha@washington.com"
                    />
                </FormControl>
                <FormControl>

                    <Button
                        disabled={isSearching}
                        onClick={doSearch}>
                        {isSearching ? "Searching" : "Search"}
                    </Button>

                </FormControl>
            </Stack>

            { results.length > 0 &&
                <List>Results: { results.length }
                    { results.map((u) => {
                        return(
                            <ListItem key={u.key}>
                                <SupportUserAvatar user={u} />
                            </ListItem>
                        );
                    }
                    ) }
                </List>
            }
        </Stack>

    );
});

export const SupportButton = ({to, title, iconName, color}:{to: string, title: string, iconName: string, color: "warning" | "danger" | undefined}) => {
    const Icon = getIcon(iconName);
    return (
        <Link to={to}>
            <Button
                color={color}
                endDecorator={<Icon color={color} />}
                variant="plain" size="sm">
                {title}
            </Button>
        </Link>
    );
};

const HomeSupportSection = observer(() => {
    const { rolesNames } = useRoles();
    if (!rolesNames.includes('support'))
        return null;

    useEffect(() => {
        document.title = "Support";
    }, []);

    return (
        <Stack spacing={3}>

            <SupportUserSearch />
            <SupportTeamActions />

        </Stack>

    );
});

export default HomeSupportSection;
